import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Building2, Trash2 } from 'lucide-react';
import { api } from '../../api.js';
import { Card, CardHeader, Button, Input, Select, Badge, EmptyState, PageHeader, Spinner } from '../../components/ui.jsx';

const DEPT_TYPES = [
  { value: '', label: 'Other / not specified' },
  { value: 'sales', label: 'Sales' },
  { value: 'account_management', label: 'Account Management' },
  { value: 'hr', label: 'HR' },
  { value: 'finance', label: 'Finance' },
  { value: 'ops', label: 'Operations' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'technology', label: 'Technology / IT' },
  { value: 'customer_service', label: 'Customer Service' },
  { value: 'leadership', label: 'Leadership' }
];

export default function OrganizationSetup() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['groups'], queryFn: api.listGroups });
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', department_type: '' });

  const createMutation = useMutation({
    mutationFn: (body) => api.createGroup(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['groups'] }); setCreating(false); setForm({ name: '', department_type: '' }); }
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => api.deleteGroup(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] })
  });

  if (isLoading) return <div className="min-h-[40vh] grid place-items-center"><Spinner className="w-8 h-8 text-primary-600" /></div>;

  const groups = data?.groups || [];

  return (
    <>
      <PageHeader
        title="Organisation"
        description="Define the departments and teams that make up your company. Groups are how permissions and dashboards are scoped."
        action={!creating ? <Button onClick={() => setCreating(true)}><Plus className="w-4 h-4 mr-1.5" />Add group</Button> : null} />

      {creating ? (
        <Card className="mb-6">
          <CardHeader title="New group" />
          <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(form); }} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <Input label="Group name" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Sales – Enterprise" />
            <Select label="Department" value={form.department_type} onChange={e => setForm(f => ({ ...f, department_type: e.target.value }))} options={DEPT_TYPES} />
            <div className="flex gap-2">
              <Button type="submit" loading={createMutation.isPending}>Create</Button>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      ) : null}

      {groups.length === 0 ? (
        <EmptyState icon={Building2} title="No groups yet"
          description="Add your first group to start organising your team. You can rearrange or rename anytime."
          action={<Button onClick={() => setCreating(true)}>Add your first group</Button>} />
      ) : (
        <Card padding={false}>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-ink-500 border-b border-ink-200">
              <tr>
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">Department</th>
                <th className="text-right py-3 px-4">Members</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <tr key={g.id} className="border-b border-ink-100 hover:bg-ink-50/50">
                  <td className="py-3 px-4 font-medium text-ink-900">{g.name}</td>
                  <td className="py-3 px-4">{g.department_type ? <Badge variant="purple">{g.department_type.replace(/_/g, ' ')}</Badge> : <span className="text-ink-400">—</span>}</td>
                  <td className="text-right py-3 px-4 text-ink-600">{g.member_count || 0}</td>
                  <td className="py-3 px-2">
                    <button onClick={() => { if (confirm(`Delete "${g.name}"? Members are not deleted.`)) deleteMutation.mutate(g.id); }}
                      className="text-ink-400 hover:text-danger transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
